import { useState, useEffect, useCallback, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type {
  MyCandidateFromAPI,
  CandidateStatusEnum,
} from "../../types/candidate";
import { CandidateStatusEnum as CandidateStatusValues } from "../../types/candidate";
import { toTitleCase } from "../../lib/utils";
import { getInitials } from "./candidate-utils";
import { useCandidateData } from "../../hooks/useCandidateData";
import { useUpdateCandidateStatus } from "../../hooks/useUpdateCandidateStatus";
import { ApiClientError } from "../../lib/api-client";
import { env } from "../../lib/env";
import { CandidateDetailModal } from "./CandidateDetailModal";
import { GreetingPreviewModal } from "./GreetingPreviewModal";
import { CandidateStatusDropdown } from "./CandidateStatusDropdown";
import { CandidateAvatarImage } from "./CandidateAvatarImage";
import { GradientSpinner } from "../common/GradientSpinner";
import { NotificationBar } from "../common/NotificationBar";
import type { NotificationColor } from "../common/NotificationBar";

interface CandidateListModalProps {
  jdPath: string;
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// Inline SVG Icons
// ---------------------------------------------------------------------------

function CloseIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M1 1L13 13M13 1L1 13"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function EmailIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 18 18"
      fill="none"
      stroke="currentColor"
      aria-hidden="true"
    >
      <rect
        x="2"
        y="4"
        width="14"
        height="10"
        rx="2"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M2 6L9 10.5L16 6"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function PlaneIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        d="M22 2L11 13M22 2L15 22L11 13M22 2L2 9L11 13"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ArrowRightIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M3.333 8H12.667M12.667 8L8.667 4M12.667 8L8.667 12"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Score Badge
// ---------------------------------------------------------------------------

function ScoreBadge({ score }: { score: number }) {
  let colorClasses: string;
  if (score >= 80) {
    colorClasses = "bg-green-50 border-green-500 text-green-600";
  } else if (score >= 50) {
    colorClasses = "bg-orange-50 border-orange-100 text-orange-500";
  } else {
    colorClasses = "bg-file-icon-bg border-red-100 text-error";
  }

  return (
    <span
      className={`w-[77px] mx-auto px-2.5 py-1 rounded-full border text-center text-body-xl font-bold tracking-tight ${colorClasses}`}
    >
      {Math.round(score)}%
    </span>
  );
}

// ---------------------------------------------------------------------------
// Contact Cell
// ---------------------------------------------------------------------------

interface ContactCellProps {
  email?: string;
  linkedinUrl?: string;
  candidateId: string;
  projectId: string;
  candidateName: string;
  canContact: boolean;
  onContactAction: (message: string, color: NotificationColor) => void;
  onRefetch: () => void;
}

function ContactCell({
  email,
  linkedinUrl: _linkedinUrl,
  candidateId,
  projectId,
  candidateName,
  canContact,
  onContactAction,
  onRefetch,
}: ContactCellProps) {
  const [greetingModalOpen, setGreetingModalOpen] = useState(false);
  const [_sendingLinkedIn, _setSendingLinkedIn] = useState(false);

  function handleEmailClick() {
    if (!email || !canContact) return;
    setGreetingModalOpen(true);
  }

  return (
    <div className="flex items-center justify-center gap-3">
      <button
        type="button"
        disabled={!email || !canContact}
        onClick={handleEmailClick}
        aria-label={
          !canContact
            ? "Contact not available for this candidate's current status"
            : email
              ? `Email ${email}`
              : "No email available"
        }
        className="size-9 shrink-0 rounded-full border border-black-50 flex items-center justify-center text-grey hover:border-black-100 hover:text-text-primary motion-safe:transition-colors disabled:opacity-40 disabled:cursor-default cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
      >
        <EmailIcon />
      </button>
      <button
        type="button"
        disabled={true}
        aria-label="LinkedIn InMail — coming soon"
        title="Coming soon"
        className="size-9 shrink-0 rounded-full border border-[#0077B7] flex items-center justify-center text-[#0077B7] disabled:opacity-40 disabled:cursor-default cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0077B7] focus-visible:ring-offset-2"
      >
        <PlaneIcon />
      </button>
      <GreetingPreviewModal
        open={greetingModalOpen}
        onClose={() => setGreetingModalOpen(false)}
        candidateId={candidateId}
        candidateName={candidateName}
        candidateEmail={email}
        projectId={projectId}
        onSuccess={(msg) => {
          setGreetingModalOpen(false);
          onContactAction(msg, "green");
          onRefetch();
        }}
        onError={(msg) => {
          onContactAction(msg, "red");
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// CandidateListModal
// ---------------------------------------------------------------------------

export default function CandidateListModal({
  jdPath,
  onClose,
}: CandidateListModalProps) {
  const {
    candidates,
    total,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
    refetch,
  } = useCandidateData(jdPath);

  const { updateStatus, isUpdating } = useUpdateCandidateStatus();
  const queryClient = useQueryClient();

  const dialogRef = useRef<HTMLDialogElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [updatingCandidateId, setUpdatingCandidateId] = useState<string | null>(
    null
  );
  const [detailCandidate, setDetailCandidate] =
    useState<MyCandidateFromAPI | null>(null);

  // Notification bar state
  const [notification, setNotification] = useState<{
    message: string;
    color: NotificationColor;
  } | null>(null);
  const notificationTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const showNotification = useCallback(
    (message: string, color: NotificationColor) => {
      if (notificationTimerRef.current) {
        clearTimeout(notificationTimerRef.current);
      }
      setNotification({ message, color });

      notificationTimerRef.current = setTimeout(() => {
        setNotification(null);
      }, 3000);
    },
    []
  );

  const dismissNotification = useCallback(() => {
    if (notificationTimerRef.current) {
      clearTimeout(notificationTimerRef.current);
    }
    setNotification(null);
  }, []);

  // Cleanup notification timer on unmount
  useEffect(() => {
    return () => {
      if (notificationTimerRef.current) {
        clearTimeout(notificationTimerRef.current);
      }
    };
  }, []);

  // Ref to keep the latest onClose without re-running the effect.
  // This avoids the useEffect re-running on every parent re-render
  // (onClose is an inline arrow function with a new identity each render).
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  // Open native dialog on mount and wire up close event.
  //
  // IMPORTANT: We intentionally do NOT call dialog.close() in the cleanup.
  //
  // Calling dialog.close() fires a synchronous native "close" event.
  // In React 19 Strict Mode (double-mount), the cleanup fires between
  // the first and second effect runs. If the "close" event is caught
  // (by a listener re-attached during remount, or via browser microtask
  // timing), it invokes onCloseRef.current() which sets
  // candidateModalOpen=false, unmounting the component before it can
  // re-open the dialog — making the modal appear to never open.
  //
  // Without dialog.close() in cleanup:
  //  - Strict Mode remount: dialog.open is still true from the first
  //    mount, so showModal() is safely skipped; we just re-add the
  //    "close" listener.  No flicker, no stale top-layer entry.
  //  - Actual unmount: React removes the DOM element, which the browser
  //    automatically removes from the top layer.  No event fires.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (!dialog.open) {
      dialog.showModal();
    }

    const handleClose = () => onCloseRef.current();
    dialog.addEventListener("close", handleClose);
    return () => {
      dialog.removeEventListener("close", handleClose);
    };
  }, []);

  // Infinite scroll — fetch next page when user scrolls near the bottom
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    function handleScroll() {
      if (!container || isFetchingNextPage || !hasNextPage) return;
      const { scrollTop, scrollHeight, clientHeight } = container;
      // Trigger when within 200px of the bottom
      if (scrollHeight - scrollTop - clientHeight < 200) {
        fetchNextPage();
      }
    }

    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => container.removeEventListener("scroll", handleScroll);
  }, [isFetchingNextPage, hasNextPage, fetchNextPage]);

  // The hook handles optimistic updates on the project candidates cache.
  // We still need to invalidate the my-candidates list so this modal refreshes.
  const handleStatusChange = useCallback(
    async (
      candidateStatusId: string,
      projectId: string,
      newStatus: CandidateStatusEnum,
      candidateId: string
    ) => {
      setUpdatingCandidateId(candidateId);
      try {
        await updateStatus({
          id: candidateStatusId,
          status: newStatus,
          projectId,
          candidateId,
        });
        // Invalidate the my-candidates infinite query so the list reflects
        // the new status after the hook has already invalidated project caches.
        void queryClient.invalidateQueries({
          queryKey: ["candidates", "my-candidates"],
        });
        showNotification("Candidate status updated successfully.", "green");
      } catch (err) {
        console.error("Failed to update status:", err);
        const message =
          err instanceof ApiClientError && err.errors.length > 0
            ? err.errors[0]
            : "Failed to update status. Please try again.";
        showNotification(message, "red");
      } finally {
        setUpdatingCandidateId(null);
      }
    },
    [updateStatus, queryClient, showNotification]
  );

  // The displayed list comes directly from the query cache — the hook's
  // optimistic update keeps it in sync during in-flight mutations.
  const displayCandidates = candidates;

  const onViewDetails = (candidate: MyCandidateFromAPI) => {
    setDetailCandidate(candidate);
  };

  return (
    <>
      <dialog
        ref={dialogRef}
        aria-label="Candidates List"
        className="m-auto w-full max-h-[85dvh] lg:h-auto lg:w-[1072px] lg:max-h-[85vh] rounded-[20px] bg-white flex flex-col items-center pt-0 px-5 pb-5 gap-[15px] touch-manipulation motion-safe:animate-modal-in backdrop:bg-[rgba(38,38,38,0.6)] backdrop:motion-safe:animate-modal-backdrop-in"
      >
        {/* Header */}
        <div className="flex w-full items-center justify-between px-4 py-4 lg:px-3 lg:py-6 border-b border-grey-50">
          <div className="flex items-center gap-2">
            <h2 className="text-display-sm font-bold tracking-tight text-black-500">
              Candidates List
            </h2>
            {total > 0 && (
              <span className="rounded-[20px] bg-bg-page px-2 text-body-md font-medium text-secondary">
                {total}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close modal"
            className="w-7 h-7 rounded-full bg-grey-50 border border-black-50 flex items-center justify-center cursor-pointer hover:bg-black-50 motion-safe:transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
          >
            <CloseIcon />
          </button>
        </div>

        {/* Notification Bar — between header and content */}
        {notification && (
          <NotificationBar
            color={notification.color}
            onDismiss={dismissNotification}
          >
            {notification.message}
          </NotificationBar>
        )}

        {/* Content */}
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-3">
              <GradientSpinner />
              <span className="bg-gradient-to-r from-gradient-start to-gradient-end bg-clip-text text-body-md font-medium text-transparent">
                Loading candidates...
              </span>
            </div>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center gap-3 py-20">
            <p className="text-body-md text-error">{error}</p>
            <button
              type="button"
              onClick={refetch}
              className="px-4 py-2 rounded-full border border-black-100 text-body-md font-bold tracking-tight text-black-500 hover:bg-grey-25 active:scale-[0.97] motion-safe:transition-[background-color,transform] motion-safe:duration-150 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
            >
              Retry
            </button>
          </div>
        ) : displayCandidates.length === 0 ? (
          <div className="flex items-center justify-center py-20">
            <p className="text-body-md text-text-secondary">
              No candidates found.
            </p>
          </div>
        ) : (
          <div className="w-full rounded-[20px] border border-black-50 overflow-hidden">
            <div
              ref={scrollContainerRef}
              className="max-h-[calc(100vh-280px)] overflow-x-auto overflow-y-auto overscroll-contain"
            >
              <table className="w-full table-fixed">
                <colgroup>
                  <col className="w-[270px]" />
                  {env.FEATURE_CONFIDENCE_SCORE_UI && (
                    <col className="w-[211px]" />
                  )}
                  <col className="w-[200px]" />
                  <col className="w-[168px]" />
                  <col className="w-[161px]" />
                </colgroup>
                <thead className="sticky top-0 z-10 bg-grey-25">
                  <tr>
                    <th className="px-3 py-[18px] text-body-md font-semibold tracking-tight text-grey text-left">
                      Candidate Name
                    </th>
                    {env.FEATURE_CONFIDENCE_SCORE_UI && (
                      <th className="px-6 py-[18px] text-body-md font-semibold tracking-tight text-grey text-center">
                        Confidence Score
                      </th>
                    )}
                    <th className="px-3 py-[18px] text-body-md font-semibold tracking-tight text-grey text-center">
                      Current Status
                    </th>
                    <th className="px-3 py-[18px] text-body-md font-semibold tracking-tight text-grey text-center">
                      Contact
                    </th>
                    <th className="px-3 py-[18px] text-body-md font-semibold tracking-tight text-grey text-center">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {displayCandidates.map((candidate, index) => {
                    const isLast = index === displayCandidates.length - 1;
                    const score = candidate.confidence_score?.overall_score;

                    return (
                      <tr
                        key={candidate.candidate_id}
                        tabIndex={0}
                        onClick={() => onViewDetails(candidate)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            onViewDetails(candidate);
                          }
                        }}
                        className={`cursor-pointer hover:bg-row-selected motion-safe:transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2${isLast ? "" : " border-b border-grey-50"}`}
                      >
                        {/* Name */}
                        <td className="px-3 py-[18px]">
                          <div className="flex items-center gap-2.5">
                            <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-r from-gradient-start to-gradient-end">
                              <CandidateAvatarImage
                                src={candidate.avatar_url}
                                alt={toTitleCase(candidate.full_name)}
                                className="size-full rounded-full object-cover"
                                fallback={
                                  <span className="text-body-xs font-bold text-white">
                                    {getInitials(
                                      toTitleCase(candidate.full_name)
                                    )}
                                  </span>
                                }
                              />
                            </div>
                            <span className="text-body-md font-semibold tracking-tight text-black-500 truncate">
                              {toTitleCase(candidate.full_name)}
                            </span>
                          </div>
                        </td>

                        {/* Confidence Score */}
                        {env.FEATURE_CONFIDENCE_SCORE_UI && (
                          <td className="px-6 py-[18px] text-center">
                            {score != null ? (
                              <ScoreBadge score={score} />
                            ) : (
                              <span className="text-body-md text-text-secondary">
                                --
                              </span>
                            )}
                          </td>
                        )}

                        {/* Status — stopPropagation to prevent row click */}
                        <td
                          className="px-3 py-[18px] text-center"
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ")
                              e.stopPropagation();
                          }}
                        >
                          <CandidateStatusDropdown
                            value={candidate.status}
                            onChange={(newStatus) => {
                              if (!candidate.id) return;
                              void handleStatusChange(
                                candidate.id,
                                candidate.project_id,
                                newStatus,
                                candidate.candidate_id
                              );
                            }}
                          />
                        </td>

                        {/* Contact — stopPropagation to prevent row click */}
                        <td
                          className="px-3 py-[18px]"
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ")
                              e.stopPropagation();
                          }}
                        >
                          <ContactCell
                            email={candidate.email}
                            linkedinUrl={candidate.linkedin_profile_url}
                            candidateId={candidate.candidate_id}
                            projectId={candidate.project_id}
                            candidateName={toTitleCase(candidate.full_name)}
                            canContact={
                              candidate.status === CandidateStatusValues.NEW ||
                              candidate.status ===
                                CandidateStatusValues.CONTACTED
                            }
                            onContactAction={(msg, color) =>
                              showNotification(msg, color)
                            }
                            onRefetch={refetch}
                          />
                        </td>

                        {/* Action — stopPropagation to prevent row click */}
                        <td
                          className="px-3 py-[18px] text-center"
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ")
                              e.stopPropagation();
                          }}
                        >
                          <button
                            type="button"
                            onClick={() => onViewDetails(candidate)}
                            className="w-[108px] h-[38px] mx-auto px-4 py-3 rounded-full border border-black-100 flex items-center justify-center gap-1.5 text-body-md font-bold tracking-tight text-black-500 hover:bg-grey-25 active:scale-[0.97] motion-safe:transition-[background-color,transform] motion-safe:duration-150 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                          >
                            Details
                            <ArrowRightIcon />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {isFetchingNextPage && (
                    <tr>
                      <td colSpan={5} className="py-4">
                        <div className="flex items-center justify-center gap-2">
                          <GradientSpinner size={16} />
                          <span className="bg-gradient-to-r from-gradient-start to-gradient-end bg-clip-text text-body-sm font-medium text-transparent">
                            Loading more…
                          </span>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </dialog>

      {/* Detail Modal — rendered as a sibling OUTSIDE the list dialog.
          Each <dialog>.showModal() creates its own top-layer entry with
          its own ::backdrop. Nesting a <dialog> inside another <dialog>
          causes the child's showModal() to fail or render behind the
          parent's backdrop, producing a "black screen". As siblings,
          each dialog independently enters the top layer and stacks
          correctly (the most-recently-opened dialog appears on top). */}
      {detailCandidate && (
        <CandidateDetailModal
          candidate={detailCandidate}
          jdPath={jdPath}
          onClose={() => setDetailCandidate(null)}
          onStatusChange={
            detailCandidate.id
              ? (newStatus) => {
                  void handleStatusChange(
                    detailCandidate.id!,
                    detailCandidate.project_id,
                    newStatus,
                    detailCandidate.candidate_id
                  );
                }
              : undefined
          }
          isStatusUpdating={
            isUpdating && updatingCandidateId === detailCandidate.candidate_id
          }
        />
      )}
    </>
  );
}
